/**
 * **Wreck conversion**: a destroyed mech/NPC leaves a RAW wreck on the board — hard cover,
 * passable difficult terrain — spawned as a live `object`-kind combatant. It spawns a NEW
 * wreck object; it does **not** kind-flip the destroyed mech, whose commit/ownership
 * breadcrumbs must survive.
 *
 * **Trigger & eligibility** (see {@link destroyedConversionKind}).
 * A resolution lands a `destroy` on a `mech`/`npc`-kind combatant → it converts:
 * - An **ordinary** (non-vaporized, non-Biological) destroy → unplace + a wreck at its
 *   footprint.
 * - A **Vaporized** destroy (reactor meltdown, Core p.81 "the wreck is annihilated") →
 *   **unplace WITHOUT a wreck**: nothing physically remains, so the dead token leaves the
 *   board and its cells open — a placed Destroyed token blocking occupancy where nothing
 *   remains is an affordance-drifts-from-reality artifact.
 * - **Biological** carriers (frozen `destructionOutcome: "corpse"`, stamped at spawn from
 *   the NPC class's `role` — see {@link "@lancer-vtt/domain".DestructionOutcome}) are skipped
 *   entirely — a destroyed monster is a corpse that stays on the board, not a mech wreck
 *   (unless ALSO Vaporized, which annihilates a creature just the same).
 * - Anything **already converted**: the eligibility scan reads `pos !== null` as the
 *   idempotency marker (mirrors `coverDestroyedObserver`'s live-requery pattern) — once a
 *   dead mech is unplaced it naturally drops off this scan and is never reprocessed, so a
 *   repeat call (or a second attack observer pass in the same resolution) is a no-op rather
 *   than double-spawning.
 *
 * **Objects never convert.** A destroyed object leaves no wreck. A destroyed map asset (a felled
 * tree) is unplaced by the sibling sweep `./assetRemains.ts`, which runs right after this one in
 * `spawnDestructionConsequences` and paints the asset's `remains` tile (`decisions/0193`).
 *
 * **The dead mech is UNPLACED, never kind-flipped.** Clearing `pos` removes it from occupancy
 * for free (movement's destroyed-object gate only excludes object/deployable kinds — a
 * *placed* Destroyed mech would otherwise double-block the wreck's cells); the combatant
 * survives in `state.order`/the UNITS rail carrying its commit breadcrumbs
 * (`sourcePrintedMechId`/`pilotId`).
 *
 * **The wreck is ONE monolithic object, not sections** — `spawnObject`, never
 * `spawnObjectGroup`. Statline is RAW's own object defaults (Core p.87) at the dead
 * mech's SIZE: `10 * SIZE` HP, 5 Evasion, 0 Armor, `{cover:"hard", blocksMovement:false,
 * moveCost:2}` — hard cover per RAW p.82, no `blocksSight` (RAW never says a wreck
 * blocks LoS), passable-but-difficult per the movement seam.
 *
 * **Splice point (this function's callers, `backend/src/encounterRoom.ts`):** the
 * `destroy` step vocabulary has no "spawn a combatant" op, and `add_combatant` is an ordinary
 * `EncounterCommand`, not a `ResolvedStep` the `AttackObserver` registry
 * (`attackObservers.ts`) can emit. Rather than widen that registry's pure
 * `(state, attacker) => ResolvedStep[]`
 * contract to also carry commands, this stays a **pure, rules-layer function returning
 * data** (the unplace step + the built wreck `Combatant`), exactly like
 * `resolveMeltdownBlast`/`resolveMeltdownTick` return steps for the room to wrap in an
 * `apply_resolved` — the room composes the actual `batch[apply_resolved, add_combatant]`
 * command in a bespoke hand-chain method (`spawnWrecks`, sibling to `detonateMeltdown`),
 * fired from the same post-attack site `attack_resolved` is raised from. This keeps the
 * observer registry's contract narrow and puts command composition where every other
 * command-composing hand-chain already lives.
 */

import {
  hasStatus,
  type Combatant,
  type CombatantId,
  type EncounterState,
  type MoveStep,
} from "@lancer-vtt/domain";
import { spawnObject } from "./spawn";

/** The canonical **Vaporized** status name — see {@link "@lancer-vtt/domain".DestroyStep.vaporized}. */
export const VAPORIZED_STATUS = "Vaporized";

/** The wreck's spatial contribution profile: hard cover, passable, difficult (moveCost 2), no blocksSight. */
export const WRECK_OBJECT_PROFILE = { cover: "hard" as const, blocksMovement: false, moveCost: 2 };

/** Mint the wreck combatant id for a given dead mech/npc id — stable, deterministic (no `crypto.randomUUID`). */
export function wreckIdFor(deadId: CombatantId): string {
  return `${deadId}:wreck`;
}

/**
 * Classify what conversion a destroyed combatant is owed **right now** (the
 * *plan-with-optional-wreck* shape). `kind !== "mech" && kind !== "npc"` is a **legitimate,
 * standing kind-read**: it's the wreck-eligibility SEAM gate, not a capability proxy —
 * `wreckConversion.test.ts` pins an NPC eligible even with a synthetically-absent structure
 * track, so this can't be re-derived from {@link "@lancer-vtt/domain".hasStructureTrack}
 * without changing behavior. The wreck-vs-corpse DEFAULT, below, is the field-based half:
 *
 *   - `"wreck"` — an ordinary (non-vaporized, non-Biological) destroyed mech/NPC:
 *     unplace the dead token AND spawn a wreck at its footprint.
 *   - `"unplace"` — a **Vaporized** carrier: unplace WITHOUT a wreck. RAW is
 *     unambiguous that vaporization leaves *nothing* — a placed Destroyed token
 *     blocking occupancy where nothing physically remains is exactly the
 *     affordance-drifts-from-reality artifact this workspace rejects. The core
 *     invariant is *unplace-on-destruction*; the wreck-spawn is the usual but not
 *     the defining companion. Vaporized (a runtime status — a reactor meltdown is a
 *     mid-fight event, never a spawn-time classifier) wins over the frozen `"corpse"`
 *     default (annihilation is annihilation — nothing remains of a vaporized creature
 *     either), though the combination is unreachable through today's emitters.
 *   - `null` — not owed a conversion: wrong kind, alive, already converted
 *     (unplaced — `pos == null` is the idempotency marker; see the file doc), or a
 *     non-vaporized `destructionOutcome: "corpse"` carrier (a destroyed monster is a
 *     corpse that stays on the board, not a mech wreck).
 */
export function destroyedConversionKind(c: Combatant): "wreck" | "unplace" | null {
  if (c.kind !== "mech" && c.kind !== "npc") return null;
  if (c.pos == null) return null; // null (unplaced/already converted) or undefined (never placed).
  if (!hasStatus(c.statuses, "Destroyed")) return null;
  if (hasStatus(c.statuses, VAPORIZED_STATUS)) return "unplace"; // dynamic override — stays a status, see the doc above.
  if (c.base.destructionOutcome === "corpse") return null; // frozen classifier default.
  return "wreck";
}

/**
 * Is `c` eligible to convert to a **wreck** right now? A placed (`pos !== null`)
 * `mech`/`npc`-kind combatant carrying `"Destroyed"` but NOT `"Vaporized"` and not a
 * frozen `destructionOutcome: "corpse"` carrier — {@link destroyedConversionKind}
 * `=== "wreck"`. A Vaporized carrier is eligible for *unplacing*, never a wreck (see the
 * classifier's doc).
 */
export function eligibleForWreck(c: Combatant): boolean {
  return destroyedConversionKind(c) === "wreck";
}

/**
 * One dead combatant's conversion plan: the unplace step, plus the wreck to add —
 * **absent for a Vaporized carrier** (unplace-without-wreck).
 */
export type WreckConversion = {
  /** The dead mech/NPC being converted. */
  deadId: CombatantId;
  /** The unplace step: `move` from the dead mech's anchor to `null`. Self-inverting. */
  unplaceStep: MoveStep;
  /**
   * The wreck combatant to add, already positioned at the dead mech's former anchor.
   * `undefined` for a Vaporized carrier — RAW p.81: "the wreck is annihilated", so
   * the conversion is the unplace alone.
   */
  wreck?: Combatant;
};

/**
 * Compute the conversion plan for every combatant in `state` owed one **right now**
 * ({@link destroyedConversionKind} non-null), sorted by dead-combatant id
 * (deterministic, replay-stable — the same discipline `coverDestroyedObserver`'s
 * sweep uses). Pure and dice-free: the wreck's stats are RAW's own flat defaults
 * keyed only off the dead mech's SIZE, so nothing here needs a `DiceSource`.
 *
 * A `"wreck"`-kind conversion carries the unplace step + the wreck; an `"unplace"`
 * -kind (Vaporized) carries the unplace step alone — the dead
 * token leaves the board and its cells open, but nothing is spawned.
 *
 * Returns `{ conversions: [], warnings: [] }` when nothing is owed (the common
 * case — most resolutions destroy nothing). A structurally-impossible `spawnObject`
 * rejection (there is none reachable here — SIZE is always the dead mech's own
 * positive SIZE, Armor is always the fixed `0`) would be a genuine bug, so it's
 * `unwrap`-equivalent: filtered out with a warning rather than thrown, keeping this
 * function total like every other resolver in this package.
 */
export function resolveWreckConversions(state: EncounterState): { conversions: WreckConversion[]; warnings: string[] } {
  const conversions: WreckConversion[] = [];
  const warnings: string[] = [];
  const dead = Object.values(state.combatants)
    .map((c) => ({ id: c.id, kind: destroyedConversionKind(c) }))
    .filter((d): d is { id: CombatantId; kind: "wreck" | "unplace" } => d.kind !== null)
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const { id: deadId, kind } of dead) {
    const c = state.combatants[deadId];
    if (!c || c.pos == null) continue; // narrowed by destroyedConversionKind; total guard for TS.
    const pos = c.pos;
    const unplaceStep: MoveStep = { op: "move", target: deadId, from: pos, to: null };

    if (kind === "unplace") {
      // Vaporized: unplace WITHOUT a wreck — nothing remains.
      conversions.push({ deadId, unplaceStep });
      warnings.push(`${c.name}: vaporized — nothing remains (token removed from the board).`);
      continue;
    }

    const spawned = spawnObject({
      combatantId: wreckIdFor(deadId),
      name: `${c.name} Wreck`,
      size: c.base.size,
      kind: "object",
      armor: 0,
      objectProfile: WRECK_OBJECT_PROFILE,
    });
    if (!spawned.ok) {
      warnings.push(`wreck conversion: ${deadId} — ${spawned.error}`);
      continue;
    }
    conversions.push({
      deadId,
      unplaceStep,
      wreck: { ...spawned.value, pos },
    });
    warnings.push(`${c.name}: destroyed and left a Size ${c.base.size} wreck (hard cover, moveCost 2).`);
  }

  return { conversions, warnings };
}
